import React from 'react';
import { Sparkles } from 'lucide-react';
import type { Badge } from '../types/badges';

interface BadgeGridProps {
  badges: Badge[];
  earnedBadges: string[];
  totalPlaytimeHours: number;
  totalAchievements: number;
}

export function BadgeGrid({ badges, earnedBadges, totalPlaytimeHours, totalAchievements }: BadgeGridProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {badges.map((badge) => {
        const isEarned = earnedBadges.includes(badge.id);
        const Icon = badge.icon;
        const progress = Math.min(100, (
          badge.category === 'playtime'
            ? (totalPlaytimeHours / badge.requirement) * 100
            : (totalAchievements / badge.requirement) * 100
        ));
        
        return (
          <div
            key={badge.id}
            className={`relative overflow-hidden rounded-lg shadow-lg transition-all ${
              isEarned ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : 'bg-white'
            }`}
          >
            {isEarned && (
              <div className="absolute top-3 right-3">
                <div className="animate-spin-slow">
                  <Sparkles className="w-6 h-6 text-yellow-300" />
                </div>
              </div>
            )}
            
            <div className="p-6">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${
                  isEarned ? 'bg-white/10' : 'bg-indigo-50'
                }`}>
                  <Icon className={`w-8 h-8 ${
                    isEarned ? 'text-white' : 'text-indigo-600'
                  }`} />
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${
                    isEarned ? 'text-white' : 'text-gray-900'
                  }`}>
                    {badge.name}
                  </h3>
                  <p className={`text-sm ${
                    isEarned ? 'text-indigo-100' : 'text-gray-600'
                  }`}>
                    {badge.description}
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className={isEarned ? 'text-indigo-100' : 'text-gray-600'}>
                    {isEarned ? 'Completed!' : `${progress.toFixed(1)}% Complete`}
                  </span>
                  <span className={isEarned ? 'text-indigo-100' : 'text-gray-600'}>
                    {badge.experiencePoints} XP
                  </span>
                </div>
                <div className={`h-2 rounded-full ${isEarned ? 'bg-white/20' : 'bg-gray-200'}`}>
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      isEarned ? 'bg-white' : 'bg-indigo-600'
                    }`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
                {!isEarned && (
                  <p className="text-sm text-gray-500 mt-2">
                    {badge.category === 'playtime' 
                      ? `${totalPlaytimeHours.toLocaleString()} / ${badge.requirement.toLocaleString()} hours`
                      : `${totalAchievements.toLocaleString()} / ${badge.requirement.toLocaleString()} achievements`
                    }
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}